import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Cloud, Eye, EyeOff, Tag, Loader2, Search } from 'lucide-react';
import { brandsAPI } from '../../lib/api';

/**
 * B2B Portal admin settings.
 *
 * Bundles all portal-wide configuration knobs that don't belong on
 * a per-brand or per-customer row:
 *   - Brand visibility (which brands appear in the public catalog)
 *   - MOQ / qty-step display (info-only — sourced from backend env vars)
 *
 * Lives as a sub-section inside the main Settings page so we keep the
 * admin nav tidy.
 */
export const B2BPortalSettings = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [savingId, setSavingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await brandsAPI.getAll();
            setBrands(data);
        } catch {
            toast.error('Failed to load brands');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const toggleVisibility = async (b, next) => {
        setSavingId(b.id);
        // Optimistic update so the switch feels instant.
        setBrands((rows) => rows.map((r) => (r.id === b.id ? { ...r, is_visible_portal: next } : r)));
        try {
            await brandsAPI.update(b.id, { is_visible_portal: next });
            toast.success(next ? `${b.name} visible in B2B portal` : `${b.name} hidden from B2B portal`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Update failed');
            setBrands((rows) => rows.map((r) => (r.id === b.id ? { ...r, is_visible_portal: !next } : r)));
        } finally {
            setSavingId(null);
        }
    };

    const filtered = search.trim()
        ? brands.filter((b) => b.name.toLowerCase().includes(search.trim().toLowerCase()))
        : brands;

    const visibleCount = brands.filter((b) => b.is_visible_portal !== false).length;

    return (
        <Card className="border-0 shadow-sm" data-testid="b2b-portal-settings-card">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <Cloud size={18} className="text-[#C08478]" />
                    B2B Portal — Brand visibility
                </CardTitle>
                <p className="text-xs text-gray-500 mt-1">
                    Choose which brands are visible to buyers in the B2B portal catalog. Hidden brands stay
                    fully usable inside the admin (POS, invoices, reports).
                </p>
                <div className="flex items-center gap-3 pt-3 flex-wrap">
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1">
                        <Eye size={11} /> Visible · {visibleCount}
                    </Badge>
                    <Badge className="bg-gray-100 text-gray-600 border border-gray-200 gap-1">
                        <EyeOff size={11} /> Hidden · {brands.length - visibleCount}
                    </Badge>
                </div>
            </CardHeader>
            <CardContent>
                <div className="relative mb-4 max-w-sm">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filter brands…"
                        className="pl-9 bg-[#FBF8F6] border-[#EFE5E1]"
                        data-testid="b2b-portal-brand-filter"
                    />
                </div>
                {loading ? (
                    <div className="py-12 text-center text-gray-400">
                        <Loader2 className="animate-spin mx-auto mb-2" size={18} /> Loading brands…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="py-10 text-center text-sm text-gray-400" data-testid="b2b-portal-brands-empty">
                        No brands match your filter.
                    </div>
                ) : (
                    <div className="divide-y divide-[#F5EEEA] border border-[#EFE5E1] rounded-md" data-testid="b2b-portal-brand-list">
                        {filtered.map((b) => {
                            const visible = b.is_visible_portal !== false;
                            return (
                                <div key={b.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`b2b-brand-row-${b.id}`}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        {b.logo_url ? (
                                            <img src={b.logo_url} alt={b.name} className="w-8 h-8 rounded object-cover bg-white border border-[#EFE5E1]" />
                                        ) : (
                                            <div className="w-8 h-8 rounded bg-[#F5EEEA] flex items-center justify-center text-[#C08478]">
                                                <Tag size={14} />
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-[#1A1A1A] truncate">{b.name}</p>
                                            <p className="text-[11px] text-gray-500 font-mono">{b.prefix} · {b.sequence_count} SKUs</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className={`text-[11px] inline-flex items-center gap-1 ${visible ? 'text-emerald-700' : 'text-gray-400'}`}>
                                            {visible ? <Eye size={11} /> : <EyeOff size={11} />}
                                            {visible ? 'Visible' : 'Hidden'}
                                        </span>
                                        <Switch
                                            checked={visible}
                                            disabled={savingId === b.id}
                                            onCheckedChange={(next) => toggleVisibility(b, next)}
                                            data-testid={`b2b-brand-portal-toggle-${b.id}`}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default B2BPortalSettings;
